/**
 * ReferralTimeline — visual timeline of a referral's lifecycle events.
 *
 * Renders a compact vertical timeline with color-coded event dots,
 * timestamps, actor labels, and inline notes. Designed to be reused
 * across:
 *   • Admin Referrals panel (legacy `status_history` shape)
 *   • Workflow orchestrator page (WorkflowEvent shape from the
 *     CareMatch pipeline: {at, kind, detail, actor})
 *
 * Accepts a normalized event shape so callers can pass either source
 * without transforming upstream:
 *   {
 *     at:      ISO timestamp string (required)
 *     status:  string label (e.g. "matched", "offered", "placed")
 *     detail:  optional inline explanation
 *     actor:   optional actor id/role (e.g. "admin", "system", email)
 *     kind:    optional short kind tag (e.g. "state_change", "sla_breach")
 *   }
 *
 * Events are rendered NEWEST FIRST. Timestamps show relative-time
 * with hover-title for the full ISO. Colors are semantic per status.
 */
import React from 'react';
import { Clock, CheckCircle2, AlertTriangle, Zap, XCircle, Sparkles } from 'lucide-react';

const STATE_COLOR = {
  // Legacy referral status_history statuses
  submitted: 'bg-sky-500 border-sky-300',
  received: 'bg-sky-500 border-sky-300',
  triaged: 'bg-sky-500 border-sky-300',
  matching: 'bg-amber-500 border-amber-300',
  matched: 'bg-amber-500 border-amber-300',
  offered: 'bg-amber-500 border-amber-300',
  scheduled: 'bg-emerald-500 border-emerald-300',
  in_care: 'bg-emerald-500 border-emerald-300',
  placed: 'bg-emerald-500 border-emerald-300',
  accepted: 'bg-emerald-500 border-emerald-300',
  completed: 'bg-emerald-600 border-emerald-300',
  closed: 'bg-slate-500 border-slate-300',
  cancelled: 'bg-rose-500 border-rose-300',
  declined: 'bg-rose-500 border-rose-300',
  no_match: 'bg-rose-500 border-rose-300',
  declined_all: 'bg-rose-500 border-rose-300',
  needs_review: 'bg-amber-500 border-amber-300',
};

function statusIcon(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('cancel') || s.includes('declined') || s.includes('no_match') || s.includes('reject')) return XCircle;
  if (s.includes('review') || s.includes('sla') || s.includes('breach') || s.includes('warn')) return AlertTriangle;
  if (s.includes('scheduled') || s.includes('placed') || s.includes('completed') || s.includes('accept')) return CheckCircle2;
  if (s.includes('match') || s.includes('offered') || s.includes('processing')) return Zap;
  return Sparkles;
}

function relativeTime(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const min = Math.round(diff / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 30) return `${day}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

function normalize(events) {
  return (events || []).map((e) => ({
    at: e.at || e.timestamp || e.created_at || null,
    status: e.status || e.kind || e.state || 'event',
    detail: e.detail || e.note || e.reason || '',
    actor: e.by_role || e.actor || e.by || '',
  }));
}

export const ReferralTimeline = ({ events, testId = 'referral-timeline', title = 'Timeline' }) => {
  const rows = normalize(events).slice().reverse(); // newest first
  if (rows.length === 0) {
    return (
      <div className="text-xs text-slate-500 italic" data-testid={`${testId}-empty`}>
        No lifecycle events yet.
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      {title && (
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
          <Clock className="w-3.5 h-3.5" />
          {title}
          <span className="ml-1 text-slate-400 dark:text-slate-500">· {rows.length} event{rows.length === 1 ? '' : 's'}</span>
        </div>
      )}
      <ol className="relative border-l-2 border-slate-200 dark:border-cn-ink-700 ml-2 space-y-3 pl-4">
        {rows.map((e, i) => {
          const colorCls = STATE_COLOR[String(e.status).toLowerCase()] || 'bg-slate-500 border-slate-300';
          const Icon = statusIcon(e.status);
          return (
            <li
              key={i}
              className="relative"
              data-testid={`${testId}-item-${i}`}
            >
              <span
                className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full ring-2 ring-white dark:ring-cn-ink-900 border ${colorCls}`}
                aria-hidden="true"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <Icon className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                <span
                  className="text-sm font-semibold capitalize text-slate-800 dark:text-slate-100"
                  data-testid={`${testId}-status-${i}`}
                >
                  {String(e.status).replace(/_/g, ' ')}
                </span>
                <span
                  className="text-xs text-slate-500 dark:text-slate-400"
                  title={e.at || ''}
                  data-testid={`${testId}-time-${i}`}
                >
                  {relativeTime(e.at)}
                </span>
                {e.actor && (
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-cn-ink-800 rounded px-1.5 py-0.5">
                    {e.actor}
                  </span>
                )}
              </div>
              {e.detail && (
                <p
                  className="mt-1 text-xs text-slate-600 dark:text-slate-300 italic pl-5"
                  data-testid={`${testId}-detail-${i}`}
                >
                  &ldquo;{e.detail}&rdquo;
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ReferralTimeline;
